import React, { useState, useEffect, Fragment } from 'react'
import Box from '@material-ui/core/Box'
import PropTypes from 'prop-types'
import { Formik, Form } from 'formik'

import useHTTPRequest from '@hooks/useHTTPRequest'
import useFeatureConfig, { FeatureNames } from '@hooks/useFeatureConfig'
import { Modal, ModalHeader, ModalSubHeader, ModalContent } from '@components/Modal'
import { Button } from '@components/Button'
import { InputFieldWithLabel, CheckboxWithLabel } from '@components/Form/Fields'
import { TrackingEvents } from '@utils/tracking'

export const ImplementScenarioModal = ({
  open,
  onClose,
  onUpdateScenario,
  scenario,
  riskProfile,
  comparedRiskProfile,
  usePresentValue,
  otpPaths,

  clientCellphoneNumber,
  editClientPath,
  clientHasIdentificationNumber,
  isClient
}) => {
  const [showVerifyRiskProfileChangeContent, setShowVerifyRiskProfileChangeContent] = useState(
    shouldShowVerifyRiskProfileChange(isClient, scenario.status, riskProfile.rating, comparedRiskProfile.rating)
  )
  const [riskChangeVerified, setRiskChangeVerified] = useState(false)

  // Ensure the verify risk profile change content is shown when
  // riskRatingChanged changes
  useEffect(() => {
    setShowVerifyRiskProfileChangeContent(
      shouldShowVerifyRiskProfileChange(isClient, scenario.status, riskProfile.rating, comparedRiskProfile.rating)
    )
    setRiskChangeVerified(false)
  }, [isClient, riskProfile.rating])

  return (
    <Modal open={open} onClose={onClose}>
      {
        showVerifyRiskProfileChangeContent
          ? <VerifyRiskProfileChangeContent
            clientCellphoneNumber={clientCellphoneNumber}
            onDecline={() => {
              setRiskChangeVerified(false)
              setShowVerifyRiskProfileChangeContent(false)
            }}
            onSuccess={() => {
              setRiskChangeVerified(true)
              setShowVerifyRiskProfileChangeContent(false)
            }}
            otpPaths={otpPaths} />
          : <CommitScenarioContent
            scenario={scenario}
            onClose={onClose}
            onUpdateScenario={onUpdateScenario}
            usePresentValue={usePresentValue}
            riskChangeVerified={riskChangeVerified}
            clientCellphoneNumber={clientCellphoneNumber}
            showRiskProfileChangeVerificationStatus={
              shouldShowVerifyRiskProfileChange(isClient, scenario.status, riskProfile.rating, comparedRiskProfile.rating)
            }
            setShowVerifyRiskProfileChangeContent={setShowVerifyRiskProfileChangeContent}
            editClientPath={editClientPath}
            clientHasIdentificationNumber={clientHasIdentificationNumber} />
      }
    </Modal>
  )
}

const isScenarioImplementable = (scenario) => scenario.planType === 'scenario' && scenario.status !== 'approved'

const shouldShowVerifyRiskProfileChange = (isClient, scenarioStatus, ratingA, ratingB) => (
  !isClient && scenarioStatus !== 'in_review' && ratingA !== ratingB
)

const buildStaticParams = ({
  scenario,
  implementScenarioEnabled, usePresentValue, scenarioStatus, riskChangeVerified,
  clientCellphoneNumber
}) => {
  const scenarioPlanTypeOrStatus = implementScenarioEnabled() && isScenarioImplementable(scenario) ? (
    { plan_type: 'implemented' }
  ) : (
    { status: 'in_review' }
  )
  const riskProfileAttributes = (riskChangeVerified && scenarioStatus !== 'in_review') ? (
    {
      risk_profile_attributes: {
        consent_given: true,
        consent_cellphone_number: clientCellphoneNumber,
      }
    }) : {}

  return {
    use_present_value: usePresentValue,
    ...scenarioPlanTypeOrStatus,
    ...riskProfileAttributes
  }
}

const CommitScenarioContent = ({
  scenario,
  onClose,
  usePresentValue,
  riskChangeVerified,
  clientCellphoneNumber,
  onUpdateScenario,
  showRiskProfileChangeVerificationStatus,
  setShowVerifyRiskProfileChangeContent,
  editClientPath,
  clientHasIdentificationNumber
}) => {
  const [
    { isEnabled: implementScenarioEnabled },
    requestScenarioReview
  ] = useFeatureConfig(
    FeatureNames.SCENARIO__IMPLEMENT,
    FeatureNames.SCENARIO__REQUEST_REVIEW,
  )
  const [loading, setLoading] = useState(false)
  const staticParams = buildStaticParams({
    scenario, implementScenarioEnabled, usePresentValue, riskChangeVerified,
    clientCellphoneNumber, scenarioStatus: scenario.status
  })
  const scenarioInReview = scenario.status === 'in_review'
  const modalSubHeader = requestScenarioReview.isEnabled() ?
    "Ask your wealth manager to approve and implement this plan." :
    "Implementing a scenario will make it the active plan."
  const callToActionButtonText = loading ? 'Loading...' : (
    implementScenarioEnabled() && isScenarioImplementable(scenario) ?
      `${scenarioInReview ? "Approve & " : ""}Implement` :
      'Send for review'
  )
  const confirmationActionText = requestScenarioReview.isEnabled() ? (
    "Are you sure you would like to send this scenario for review?"
  ) : (
    scenarioInReview ?
      "Your client has requested to implement this scenario. Do you want to approve and implement this plan? If you're unsure, review the plan again or contact your client." :
      "Are you sure you would like to implement this scenario?"
  )
  const trackAs = implementScenarioEnabled() ? (
    scenarioInReview ? TrackingEvents.scenario.approve : TrackingEvents.scenario.implement
  ) : null

  return (
    <Fragment>
      <ModalHeader>Implement scenario</ModalHeader>

      <ModalSubHeader>{modalSubHeader}</ModalSubHeader>

      <ModalContent>
        <Formik
          initialValues={{
            ...staticParams,
            name: scenario.name,
            send_implementation_notification_to_client: false,
          }}
          onSubmit={(values) => {
            setLoading(true)
            onUpdateScenario(values)
          }}>
          <Form className='app-form'>
            <p className='text-center'>
              {confirmationActionText}
            </p>

            <Box className='flex flex-row items-center justify-center mb-4'>
              <InputFieldWithLabel
                name='name'
                label='Scenario name'
                style={{ width: '80%' }} />
            </Box>

            {(implementScenarioEnabled() && scenario.status !== 'in_review') &&
              <Box className='flex flex-row items-center justify-center mb-4'>
                <CheckboxWithLabel
                  name='send_implementation_notification_to_client'
                  label='Notify client of changes when implementing this plan'
                  style={{ width: '80%' }} />
              </Box>
            }

            <Box
              display='flex'
              justifyContent='center'
              className='flex justify-center gap-4'>
              <Button secondary onClick={onClose}>Cancel</Button>
              <Button primary type='submit'
                disabled={!clientHasIdentificationNumber || loading}
                trackAs={trackAs}>
                {callToActionButtonText}
              </Button>
            </Box>

            {!clientHasIdentificationNumber &&
              <Box
                display='flex'
                justifyContent='center'
                className='flex justify-center gap-4 mt-4'>
                <p style={{ marginBottom: 0, textAlign: 'center' }}>
                  <small style={{ color: 'red' }}>Client ID number missing:</small>

                  <Button textLink href={editClientPath}>
                    <small>Update Client Details</small>
                  </Button>
                </p>
              </Box>
            }

            {showRiskProfileChangeVerificationStatus &&
              <Box
                display='flex'
                justifyContent='center'
                className='flex justify-center gap-4 mt-4'>
                <p style={{ marginBottom: 0 }}>
                  <small>Risk profile change verified with client? {riskChangeVerified ? 'Yes' : 'No'}</small>

                  {!riskChangeVerified &&
                    <Button textLink onClick={() => setShowVerifyRiskProfileChangeContent(true)} style={{ marginLeft: "0.5rem" }}>
                      <small>Verify with client OTP</small>
                    </Button>
                  }
                </p>
              </Box>
            }
          </Form>
        </Formik>
      </ModalContent>
    </Fragment>
  )
}

const VerifyRiskProfileChangeContent = ({ onDecline, onSuccess, otpPaths, clientCellphoneNumber }) => {
  const [otpConfirmationVisible, setOtpConfirmationVisible] = useState(false)
  const showOtpConfirmation = () => setOtpConfirmationVisible(true)

  if (otpConfirmationVisible) {
    return (
      <OtpConfirmationContent
        clientCellphoneNumber={clientCellphoneNumber}
        onSuccess={onSuccess}
        otpPaths={otpPaths} />
    )
  }

  return (
    <Fragment>
      <ModalHeader>Risk profile change detected</ModalHeader>

      <ModalContent>
        <Box className='mb-4'>
          <p className='text-center'>Changing the risk profile will greatly impact the outcome of the financial projections.</p>
          <p className='text-center'>
            <b>
              We strongly suggest that you confirm this change with your client by sending a One Time Pin to their phone.
            </b>
          </p>
        </Box>

        <Box className='flex justify-center gap-4'>
          <Button secondary onClick={onDecline}>Skip confirmation</Button>
          <Button primary onClick={showOtpConfirmation}>
            Confirm with client OTP
          </Button>
        </Box>
      </ModalContent>
    </Fragment>
  )
}

const OtpConfirmationContent = ({ onSuccess, otpPaths, clientCellphoneNumber }) => {
  const [initialOtp, setInitialOtp] = useState('')
  const [hasError, setHasError] = useState(false)
  const getOtp = useHTTPRequest({
    url: otpPaths.requestOtpPath,
    method: 'POST',
    onStart: () => setHasError(false),
    onSuccess: (data) => {
      setInitialOtp(data.otp_code)
      setHasError(false)
    },
    onError: () => setHasError(true)
  })
  const resendOtp = useHTTPRequest({
    url: otpPaths.resendOtpPath,
    method: 'POST',
    onStart: () => setHasError(false),
    onSuccess: (data) => {
      setInitialOtp(data.otp_code)
      resendOtp.reset({ resetState: false })
    },
    onError: () => setHasError(true)
  })
  const verifyOtp = useHTTPRequest({
    url: otpPaths.verifyOtpPath,
    method: 'POST',
    onStart: () => setHasError(false),
    onSuccess,
    onError: () => setHasError(true)
  })
  const loading = getOtp.ready || getOtp.loading || verifyOtp.loading || resendOtp.loading

  useEffect(() => {
    (async () => {
      await getOtp.execute()
    })()
  }, [])

  return (
    <Fragment>
      <ModalHeader>One-Time Pin confirmation</ModalHeader>

      <ModalContent>
        <p>
          We have sent an OTP to the client's mobile number that we have on record: +{clientCellphoneNumber}
        </p>
        <Formik
          enableReinitialize={true}
          onSubmit={values => {
            verifyOtp.execute({
              two_factor_auth_sessions: { one_time_pin: values.otp }
            })
          }}
          initialValues={{ otp: initialOtp }}>
          <Form className='app-form'>
            <Box className='mb-4'>
              <Box className='flex flex-row justify-center'>
                <InputFieldWithLabel
                  name='otp'
                  label='Enter your OTP'
                  style={{ width: '80%' }} />
              </Box>

              {hasError &&
                <Box className='flex justify-center'>
                  <p style={{ color: '#bf616a', width: '80%' }}>
                    Your OTP did not match or there was a problem verifying the OTP. Please try again or contact support if the issue persists.
                  </p>
                </Box>}
            </Box>

            <Box className='flex justify-center gap-4'>
              <Button secondary onClick={() => resendOtp.execute()} disabled={loading}>
                {resendOtp.loading ? 'Loading...' : 'Resend'}
              </Button>

              <Button primary type='submit' disabled={loading}>
                {verifyOtp.loading ? 'Loading...' : 'Submit'}
              </Button>
            </Box>
          </Form>
        </Formik>
      </ModalContent>
    </Fragment>
  )
}

ImplementScenarioModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdateScenario: PropTypes.func.isRequired,
  scenario: PropTypes.shape({
    name: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired
  }),
  usePresentValue: PropTypes.bool.isRequired,
  riskProfile: PropTypes.shape({ rating: PropTypes.number.isRequired }).isRequired,
  comparedRiskProfile: PropTypes.shape({ rating: PropTypes.number.isRequired }).isRequired,
  otpPaths: PropTypes.exact({
    requestOtpPath: PropTypes.string.isRequired,
    verifyOtpPath: PropTypes.string.isRequired,
    resendOtpPath: PropTypes.string.isRequired
  }).isRequired,
  clientCellphoneNumber: PropTypes.string.isRequired,
  clientHasIdentificationNumber: PropTypes.bool.isRequired,
  isClient: PropTypes.bool.isRequired
}
